import {
  InitAppOptions,
  PlatformApiMethods,
} from '@wix/editor-platform-host-integration-apis';
import { IEditorPlatformPackagesBridge } from '@wix/editor-platform-interfaces';
import { IPlatformInfraPublicAPI } from '@wix/editor-platform-worker';
import { EditorSubType } from '@wix/editor-platform-sdk-types';
import { DocumentServicesObject } from '@wix/document-services-types';
import { platformEvents } from '../../platformEvents';
import * as flowPlatformApps from './flowPlatformApps';
import * as platformPostMessageService from './platformPostMessageService';
import workerService from '../services/workerService';
import { init as initCanvasOverlayService } from '../services/canvasOverlayService';
import { enhance } from './platformApiEnhancer';
import { IWorkspaceApiAdapter } from '../utils/workspaceApiAdapter';
import { IExperimentsApiAdapter } from '../utils/experimentsApiAdapter';
import { init as initToolsEntryService } from '../services/toolsEntryService';
import { init as initViewStateService } from '../services/viewStateService';
import { init as initFeedbackLoopService } from '../services/feedbackService';
import {
  createBiLogger,
  reportBi,
  PLATFORM_INIT,
  PLATFORM_ERROR,
} from '../../essentials';
import { initialize as initI18n, setLanguage } from '../../essentials/i18n';
import { createUrlDecorator } from '../utils/urlDecorator';
import { getEditorSubType, getHostTypeFromInitOptions } from '../../utils';
import { componentRemovedHook } from '../services/unifiedComponents/editorExperiences/removeComponent';
import { initEditActionsServices } from '../services/editActionsServices';
import { initStateResetHandler } from '../services/stateResetHandler';

import 'reflect-metadata';
import { initIntentInstallServices } from '../services/intentInstallService';
import { initNewPlatformInfra } from './initNewPlatformInfra';
import { initPluginsServices } from '../services/plugins/init';

import { initSdkEventHandlerService } from '../services/SdkEventHandlerService/init';
import {
  initAppRefreshService,
  EventListenerStrategy,
} from '../services/SdkEventHandlerService';
import { buildRouterRefreshPayload } from '../services/SdkEventHandlerService/services/appRefreshService/appRefreshService';
import type { AppRefreshService } from '../services/SdkEventHandlerService/services/appRefreshService/appRefreshService';
import { BackwardCompatibleComponentsPlatformService } from '@wix/editor-platform-manifest-services';
import type { ComponentsPlatformService } from '@wix/editor-platform-manifest-services';
import type { IntentInstallServices } from '../services/intentInstallService/IntentInstallServices';
import { appMarketService } from '../utils/appMarketService';
import { initPlatformRealtimeRouterRefreshService } from '../services/platformRealtimeRouterRefreshService/platformRealtimeRouterRefreshService';
import { EditorPointerServiceAPI } from '@wix/editor-document-logic-api';

initI18n({
  locale: 'en',
});

let platformInfraPromiseResolve: (api: IPlatformInfraPublicAPI) => void;

const platformInfraPromise = new Promise<IPlatformInfraPublicAPI>((resolve) => {
  platformInfraPromiseResolve = resolve;
});

export async function getWorkerManager() {
  console.warn('"getWorkerManager" is deprecated, use "getPlatformInfraAPI"');
  return platformInfraPromise;
}

export async function getPlatformInfraAPI() {
  return platformInfraPromise;
}

export function initNewPlatform(
  apis: {
    documentServicesAPI: DocumentServicesObject;
    workspaceAPI: IWorkspaceApiAdapter;
    experimentsAPI: IExperimentsApiAdapter;
  },
  platformApiMethods: PlatformApiMethods,
  packagesBridge: Partial<IEditorPlatformPackagesBridge>,
  initAppOptions?: InitAppOptions,
) {
  const infra = initNewPlatformInfra({
    // @ts-expect-error these fns need to be typed as HostApi from SDK 2.0
    host:
      window.commonConfig?.host?.toLowerCase() ||
      getHostTypeFromInitOptions(initAppOptions),
    // TODO: we will require editor to pass HostAPI in future, for now we just wrap it and add types for what we use.
    __temporaryPlatformApiMethods: platformApiMethods,
    documentServicesAPI: apis.documentServicesAPI,
    experimentsAPI: apis.experimentsAPI,
    editorPlatformPackagesBridge: packagesBridge,
  });
  platformInfraPromiseResolve(infra.getPublicAPI());

  return infra;
}

export const getBiLoggerInitData = (
  documentServicesAPI: DocumentServicesObject,
  platformApiMethods: PlatformApiMethods,
) => ({
  userId: documentServicesAPI.generalInfo.getUserInfo().userId,
  metaSiteId: documentServicesAPI.generalInfo.getMetaSiteId(),
  editorSessionId: platformApiMethods.editor.info.getEditorSessionId(),
  editorType: platformApiMethods.editor.info.getHostType(),
  targetAccountId:
    window?.responsiveEditorModel?.identity.ownerId ??
    window?.editorModel?.permissionsInfo.ownerId,
});

export function init(
  apis: {
    documentServicesAPI: DocumentServicesObject;
    workspaceAPI: IWorkspaceApiAdapter;
    experimentsAPI: IExperimentsApiAdapter;
    editorPlatformPackagesBridge: Partial<IEditorPlatformPackagesBridge>;
  },
  platformApiMethods: PlatformApiMethods,
  editorPointerAPI?: EditorPointerServiceAPI,
  initAppOptions?: InitAppOptions,
  extraNamespaces?: string[],
): {
  platformApiMethods: PlatformApiMethods;
  backwardCompatibleComponentsPlatformService: BackwardCompatibleComponentsPlatformService;
  componentsPlatformService: ComponentsPlatformService;
  intentInstallServices: IntentInstallServices;
  editActionsServices: ReturnType<typeof initEditActionsServices>;
  pluginsServices: ReturnType<typeof initPluginsServices>;
  appRefreshService: AppRefreshService;
} {
  const {
    workspaceAPI,
    documentServicesAPI,
    experimentsAPI,
    editorPlatformPackagesBridge,
  } = apis;
  try {
    createBiLogger(
      getBiLoggerInitData(documentServicesAPI, platformApiMethods),
    );
  } catch (e) {
    console.error('failed to create bi logger', e);
  }

  const currentLanguage = documentServicesAPI.generalInfo.getLanguage();
  setLanguage(currentLanguage);

  enhance(platformApiMethods, {
    documentServices: documentServicesAPI,
    experimentsAPI,
    urlDecorator: createUrlDecorator(initAppOptions),
  });

  reportBi(PLATFORM_INIT, {
    step: 'start',
  });

  const isBuilderServicesEnabled =
    experimentsAPI.enabled('specs.responsive-editor.enableManifestServices') &&
    (initAppOptions?.origin?.type === 'RESPONSIVE' ||
      initAppOptions?.origin?.type === 'EDITOR_3');

  if (!editorPointerAPI || Object.keys(editorPointerAPI).length === 0) {
    console.error('editorPointerAPI is empty', editorPointerAPI);
  }

  // this can only happen in classic editor - where editor pointer is faked on the editor side, and there's no bridge.
  if (!editorPlatformPackagesBridge.EditorPointerAPI) {
    editorPlatformPackagesBridge.EditorPointerAPI = editorPointerAPI;
  }

  const platformInfra = initNewPlatform(
    apis,
    platformApiMethods,
    editorPlatformPackagesBridge,
    initAppOptions,
  );

  platformInfra
    .startApps()
    .then((x) => {
      reportBi(PLATFORM_INIT, {
        step: 'end',
      });

      // Trigger appMarket.getData for all initially loaded apps
      appMarketService.triggerAppMarketDataFetchForInitialApps(
        documentServicesAPI,
      );

      return x;
    })
    .catch((e: any) => {
      console.error(e);

      reportBi(PLATFORM_ERROR, {
        errorMessage: e.toString(),
        errorDetails: e.stack,
        name: e.name,
        errorType: e.name,
        errorCode: e.code,
      });
    });

  if (isBuilderServicesEnabled) {
    platformInfra.startManifestServices();
  }

  const apiForSDK = documentServicesAPI.platform.getAPIForSDK(
    platformApiMethods,
  ) as PlatformApiMethods;

  platformPostMessageService.setEditorAPI(
    documentServicesAPI,
    'editorAPI',
    apiForSDK,
  );

  // TODO: remove this when we have a better way to refresh the routers
  if (platformApiMethods.editor.routers) {
    const origRefresh = platformApiMethods.editor.routers.refresh;
    platformApiMethods.editor.routers.refresh = async (
      appData,
      _t,
      options?: { selectedRoute?: string },
    ) => {
      apis.editorPlatformPackagesBridge.PlatformEventsEmitter?.emit(
        'routers.refreshed',
        buildRouterRefreshPayload({
          appDefinitionId: appData?.appDefinitionId,
          selectedRoute: options?.selectedRoute,
        }),
      );
      origRefresh?.(appData, _t);
    };
  }

  const appsContainer: Worker = documentServicesAPI.platform.init(
    platformApiMethods,
    { extraNamespaces },
    initAppOptions,
  );

  workerService.setWorker('internal', appsContainer);

  flowPlatformApps.runFlowPlatformApps(documentServicesAPI);

  const onSetAppAPI = ({ apiName }: { apiName: string }) =>
    platformPostMessageService.onSetAppPublicAPI(documentServicesAPI, apiName);

  documentServicesAPI.platform.registerToPublicApiSet(onSetAppAPI);
  documentServicesAPI.platform.registerToPrivateApiSet(onSetAppAPI);

  documentServicesAPI.platform.registerToComponentRemoved(
    componentRemovedHook(
      platformApiMethods,
      documentServicesAPI,
      experimentsAPI,
    ),
  );

  initCanvasOverlayService(
    platformApiMethods,
    documentServicesAPI,
    workspaceAPI,
    initAppOptions?.origin?.type === 'RESPONSIVE',
  );

  const componentsService = platformInfra.getComponentsService();

  const backwardCompatibleComponentsPlatformService = isBuilderServicesEnabled
    ? new BackwardCompatibleComponentsPlatformService(componentsService)
    : BackwardCompatibleComponentsPlatformService.getPact();

  const editActionsServices = initEditActionsServices(
    documentServicesAPI,
    componentsService,
    platformApiMethods,
    isBuilderServicesEnabled,
    editorPlatformPackagesBridge,
    editorPointerAPI,
  );

  initToolsEntryService(documentServicesAPI);

  // TODO: remove initViewStateService once new states approach is used across all editors
  if (getEditorSubType() !== EditorSubType.Editor3) {
    initViewStateService(documentServicesAPI);
  } else if (isBuilderServicesEnabled) {
    initStateResetHandler(documentServicesAPI, componentsService);
  }
  initFeedbackLoopService(documentServicesAPI);

  // builder related services initialization end

  const intentInstallServices = initIntentInstallServices(
    platformApiMethods,
    documentServicesAPI,
    experimentsAPI,
  );

  // don't initiate event propagation in non-browser environments (i.e. unit tests)
  if (typeof MessageChannel !== 'undefined') {
    platformEvents.propagation.initMainFrame();
  }

  const pluginsServices = initPluginsServices(
    documentServicesAPI,
    experimentsAPI,
  );

  const appRefreshService = initAppRefreshService(
    documentServicesAPI,
    editorPlatformPackagesBridge,
    platformApiMethods,
  );

  initPlatformRealtimeRouterRefreshService(
    documentServicesAPI,
    platformInfra.getPublicAPI(),
    appRefreshService,
  );

  initSdkEventHandlerService(
    EventListenerStrategy.Browser,
    documentServicesAPI,
    experimentsAPI,
    platformApiMethods,
    appRefreshService,
  );

  return {
    platformApiMethods,
    backwardCompatibleComponentsPlatformService,
    componentsPlatformService: componentsService,
    intentInstallServices,
    editActionsServices,
    pluginsServices,
    appRefreshService,
  };
}
